import axios from 'axios';
import { getAuthHeaders } from './index';

// Use the same API base as ReviewsV2 / ReviewsSettings
const API_URL =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

/**
 * Fetch the Business Profile (Source of Truth) for a given location.
 */
export const getListingProfile = async (locationId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/listing/profile/${locationId}`,
      {
        headers
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching listing profile:', error);
    throw error;
  }
};

/**
 * Update or Create the Business Profile (Source of Truth) for a given location.
 */
export const updateListingProfile = async (locationId, profileData) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.put(
      `${API_URL}/listing/profile/${locationId}`,
      profileData,
      {
        headers
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating listing profile:', error);
    throw error;
  }
};

/**
 * Trigger an async Citation Audit across all directories.
 */
export const triggerListingAudit = async (locationId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/listing/audit/${locationId}`,
      {},
      {
        headers
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error triggering listing audit:', error);
    throw error;
  }
};

/**
 * Fetch the exact statuses, audit scores, and missing citations.
 */
export const getListingAuditResults = async (locationId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/listing/audit/${locationId}`, {
      headers
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching listing audit results:', error);
    throw error;
  }
};

/**
 * Trigger an outbound sync to all configured external APIs (Google, Yelp, etc).
 */
export const triggerListingSync = async (locationId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/listing/sync/${locationId}`,
      {},
      {
        headers
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error triggering listing sync:', error);
    throw error;
  }
};

/**
 * Get ongoing sync/submission job statuses.
 */
export const getListingSyncStatus = async (locationId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/listing/sync/status/${locationId}`,
      {
        headers
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching listing sync status:', error);
    throw error;
  }
};

/**
 * Get active alerts or conflicts.
 */
export const getListingAlerts = async (locationId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/listing/alerts/${locationId}`,
      {
        headers
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching listing alerts:', error);
    throw error;
  }
};

// ─── Foursquare OAuth ────────────────────────────────────

/**
 * Get Facebook config (App ID) for building the OAuth URL.
 */
export const getFacebookConfig = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/facebook/config`, {
      headers
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Facebook config:', error);
    throw error;
  }
};

/**
 * Start async Facebook code exchange job (prevents API Gateway timeout issues).
 */
export const startFacebookExchange = async (locationId, code, redirectUri) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/facebook/start-exchange`,
      { locationId, code, redirectUri },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error starting Facebook exchange:', error);
    throw error;
  }
};

/**
 * Poll status of async Facebook code exchange job.
 */
export const getFacebookExchangeStatus = async (locationId, jobId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/facebook/exchange-status`, {
      headers,
      params: { locationId, jobId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Facebook exchange status:', error);
    throw error;
  }
};

/**
 * Save selected Facebook page credentials for the client location.
 */
export const saveFacebookPage = async (
  locationId,
  pageId,
  pageName,
  pageAccessToken
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/facebook/save-page`,
      { locationId, pageId, pageName, pageAccessToken },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error saving Facebook page:', error);
    throw error;
  }
};

/**
 * Get Foursquare config (Client ID) for building the OAuth URL.
 */
export const getFoursquareConfig = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/foursquare/config`, {
      headers
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Foursquare config:', error);
    throw error;
  }
};

/**
 * Exchange Foursquare auth code for token + fetch managed venues.
 */
export const exchangeFoursquareCode = async (code, redirectUri) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/foursquare/exchange-code`,
      { code, redirectUri },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error exchanging Foursquare code:', error);
    throw error;
  }
};

/**
 * Save the selected Foursquare venue to DynamoDB.
 */
export const saveFoursquareVenue = async (
  locationId,
  venueId,
  venueName,
  venueAddress,
  accessToken
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/foursquare/save-venue`,
      { locationId, venueId, venueName, venueAddress, accessToken },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error saving Foursquare venue:', error);
    throw error;
  }
};

// ─── Bing Places OAuth ──────────────────────────────────

/**
 * Get Bing OAuth config (Client ID / Tenant ID / Scope) for building the auth URL.
 */
export const getBingConfig = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/bing/config`, {
      headers
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Bing config:', error);
    throw error;
  }
};

/**
 * Exchange Microsoft Entra authorization code for tokens and account details.
 */
export const exchangeBingCode = async (code, redirectUri, codeVerifier) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/bing/exchange-code`,
      { code, redirectUri, codeVerifier },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error exchanging Bing code:', error);
    throw error;
  }
};

const parseJwtPayload = (token) => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

/**
 * Exchange Microsoft auth code directly from browser for SPA-type app registrations.
 */
export const exchangeBingCodeInBrowser = async (
  code,
  redirectUri,
  codeVerifier,
  clientId,
  tenantId,
  scope
) => {
  try {
    const tokenEndpoint = `https://login.microsoftonline.com/${encodeURIComponent(
      tenantId || 'common'
    )}/oauth2/v2.0/token`;

    const tokenParams = new URLSearchParams();
    tokenParams.append('client_id', clientId);
    tokenParams.append('code', code);
    tokenParams.append('redirect_uri', redirectUri);
    tokenParams.append('grant_type', 'authorization_code');
    tokenParams.append('scope', scope);
    tokenParams.append('code_verifier', codeVerifier);

    const tokenRes = await axios.post(tokenEndpoint, tokenParams.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: 15000
    });

    const claims = parseJwtPayload(tokenRes.data?.id_token);

    return {
      message: 'Authorization successful.',
      accessToken: tokenRes.data?.access_token,
      refreshToken: tokenRes.data?.refresh_token || '',
      expiresIn: tokenRes.data?.expires_in,
      scope: tokenRes.data?.scope || scope,
      account: {
        id: claims?.oid || claims?.sub || '',
        displayName: claims?.name || '',
        email: claims?.email || claims?.preferred_username || ''
      }
    };
  } catch (error) {
    console.error('Error exchanging Bing code in browser:', error);
    throw error;
  }
};

/**
 * Save Bing connection metadata and tokens for a location.
 */
export const saveBingConnection = async (
  locationId,
  accountId,
  accountName,
  accountEmail,
  accessToken,
  refreshToken,
  scope
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/bing/save-connection`,
      {
        locationId,
        accountId,
        accountName,
        accountEmail,
        accessToken,
        refreshToken,
        scope
      },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error saving Bing connection:', error);
    throw error;
  }
};
